import { getNestedSubelementsByType, getNestedFloatingElementsByType } from '@fs/zion-sls'
import { baseUrl } from '@fs/zion-config'
import getFormattedDate, { dateFormats } from '../../../../shared/utils/dateFormatHelper'

/**
 * Generates a formatted title for an R2 image group based on its title, coverages, and associated metadata.
 *
 * @param {Object} group - The group object containing title and image viewer coverages.
 *
 * @returns {string} - The formatted title for the R2 image group.
 */
export function getImageTitleFromGroup(group) {
  let title = group?.title ?? ''
  const coverages = group?.imageViewerCoverages

  if (title && coverages?.length) {
    title += ': '
  }

  if (coverages) {
    coverages.forEach((coverage, i) => {
      if (coverage.place?.name) {
        if (i > 0) {
          title += ' | '
        }
        title += `${coverage.place.name}.`

        if (coverage.recordTypes) {
          coverage.recordTypes.forEach((recordType, j) => {
            if (j > 0) {
              title += ','
            }
            if (recordType.name) {
              title += ` ${recordType.name}`
            }

            if (recordType.date?.length) {
              title += ` ${recordType.date
                ?.map(
                  (date) =>
                    date?.formatted || getFormattedDate(date.fromTimestamp, date.toTimestamp, dateFormats.formatted)
                )
                .filter(Boolean)
                .join(', ')}`
            }
          })
        }
      }
    })
  }
  return title
}

/**
 * Generates an array of attachment URLs for persons associated with the current image.
 *
 * @param {Object} currentImage - The current image object containing sub-elements and floating elements.
 *
 * @returns {Object} - An object containing the `attachmentUrls`.
 * @returns {string[]} attachmentUrls - Array of URLs for attachments associated with persons.
 */
export function getAttachmentsUrls(currentImage) {
  const urlList = []
  const persons = getNestedSubelementsByType(currentImage, 'PERSON') || []
  persons.forEach((person) => {
    urlList.push(person.id)
  })
  // You can attach people from records on other images on this page, so we've got to load their attachments.
  const floatingPersons = getNestedFloatingElementsByType(currentImage, 'PERSON') || []
  floatingPersons.forEach((person) => {
    urlList.push(person.id)
  })

  const attachmentUrls = urlList.map((ark) => `${baseUrl}/ark:/61903/${ark}`)

  return attachmentUrls
}

/**
 * Generates an array of unique Ark IDs for persons associated with the current image.
 *
 * @param {Object[]} allRecords - Array of records containing sub-elements.
 * @param {string} currentImageArk - The Ark ID of the current image.
 *
 * @returns {Object} - An object containing the `arkIdArray`.
 * @returns {string[]} arkIdArray - Array of unique Ark IDs for persons associated with the current image.
 */
export function getArkIdArray(allRecords, currentImageArk) {
  if (allRecords.length > 0) {
    const buildArkIds = allRecords.map((record) => {
      const persons =
        record?.subElements?.filter((sub) => sub?.elementType === 'PERSON' && sub?.partitionKey === currentImageArk) ||
        []
      return persons.map((person) => person.id.slice(4))
    })
    const arkIdArray = [...new Set(buildArkIds.flat())]
    return arkIdArray
  }
  return []
}

/**
 * Checks if two arrays are equal by comparing their sorted elements.
 *
 * @param {Array} a - The first array to compare.
 * @param {Array} b - The second array to compare.
 * @returns {boolean} Returns true if both arrays contain the same elements (regardless of order), false otherwise.
 */
export const arraysEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false
  if (a.length !== b.length) return false
  const aSorted = [...a].sort()
  const bSorted = [...b].sort()
  return aSorted.every((val, idx) => val === bSorted[idx])
}

export default getImageTitleFromGroup
